import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Collapse,
} from "@mui/material";
import { Form } from "react-final-form";
import { useNavigate } from "react-router-dom";
import { Radios } from "mui-rff";

import { useTranslation } from "@/lib/i18n";
import { TextField, Select, showToast } from "@/features/shared/components";
import {
  useCreateFolderMutation,
  useCreatePageMutation,
} from "@/features/admin/features/pages-management/mutations";
import { useAdminDashboard } from "@/features/admin/hooks";
import { Icon, NavigationElement } from "@/lib/api";
import IconSelector from "@/features/admin/components/icon-selector/icon-selector";

interface FormValues {
  name: string;
  parent_id: number;
  type: "folder" | "page";
  icon: Icon;
}

type Props = {
  open: boolean;
  handleClose: () => void;
  isFolder?: boolean;
  availableFolders: NavigationElement[];
};

const createMutationBody = (values: FormValues, dashboardId: string) => {
  const { name, parent_id, type, icon } = values;

  return {
    dashboardId,
    body: {
      title: name,
      icon,
      ...(type === "page" && {
        parent_id: String(parent_id),
      }),
    },
  };
};

export default function CreateNaivgationElementDialog({
  availableFolders,
  handleClose,
  open,
}: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dashboard = useAdminDashboard();
  const { mutate: createFolderMutation } = useCreateFolderMutation();
  const { mutate: createPageMutation } = useCreatePageMutation();

  async function onSubmit(values: FormValues) {
    const { type } = values;

    if (type === "folder") {
      createFolderMutation(createMutationBody(values, dashboard.id), {
        onSuccess: () => {
          showToast(t("admin.messages.folder_created"));
          handleClose();
        },
        onError: (err) => {
          console.log(err);
        },
      });
    } else {
      createPageMutation(createMutationBody(values, dashboard.id), {
        onSuccess: ({ data }) => {
          showToast(t("admin.messages.page_created"));
          handleClose();
          navigate(`../page/${data.page_id}`);
        },
        onError: (err) => {
          console.log(err);
        },
      });
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <Form<FormValues>
        onSubmit={onSubmit}
        initialValues={{
          type: "page",
          parent_id: 0,
          icon: {
            type: "symbol",
            variant: "file",
          },
        }}
        render={({ handleSubmit, values }) => (
          <>
            <DialogTitle>{t("admin.pages.new_dialog.title")}</DialogTitle>
            <DialogContent>
              <form onSubmit={handleSubmit} noValidate>
                <Radios
                  key="type"
                  name="type"
                  required
                  data={[
                    {
                      label: t("admin.pages.new_dialog.fields.type.page"),
                      value: "page",
                    },
                    {
                      label: t("admin.pages.new_dialog.fields.type.folder"),
                      value: "folder",
                    },
                  ]}
                />

                <TextField
                  margin="dense"
                  name="name"
                  label={
                    values.type === "folder"
                      ? t("admin.pages.new_dialog.fields.name.label.folder")
                      : t("admin.pages.new_dialog.fields.name.label.page")
                  }
                  required
                />

                <Collapse in={availableFolders.length > 0 && values.type === "page"}>
                  <div>
                    <Select
                      label={t("admin.pages.new_dialog.fields.sub_page.label")}
                      name="parent_id"
                      options={[
                        {
                          label: t("admin.pages.new_dialog.fields.sub_page.none"),
                          value: 0,
                        },
                        ...availableFolders.map((v) => {
                          return {
                            label: v.title,
                            value: v.id,
                          };
                        }),
                      ]}
                    />
                  </div>
                </Collapse>

                <Select
                  label="Icon"
                  options={[
                    {
                      label: "Symbol",
                      value: "symbol",
                    },
                    {
                      label: "Abbreviation",
                      value: "abbreviation",
                    },
                  ]}
                  name="icon.type"
                />

                {values.icon?.type === "symbol" && <IconSelector name="icon.variant" />}
                {values.icon?.type === "abbreviation" && (
                  <TextField
                    margin="dense"
                    name="icon.content"
                    placeholder="Abbreviation"
                    required={values.icon?.type === "abbreviation"}
                  />
                )}
              </form>
            </DialogContent>
            <DialogActions>
              <Button key="cancel" onClick={handleClose}>
                {t("general.cancel")}
              </Button>
              <Button key="save" onClick={handleSubmit}>
                {t("general.save")}
              </Button>
            </DialogActions>
          </>
        )}
      />
    </Dialog>
  );
}
